class AgMainBlog extends HTMLElement {
  connectedCallback() {
    if (this.lifecycle) return;
    this.lifecycle = new AbortController();
    this.addEventListener('click', (event) => this.handleClick(event), { signal: this.lifecycle.signal });
    window.addEventListener('popstate', () => this.renderCategory(window.location.href, { historyMode: 'none' }), { signal: this.lifecycle.signal });

    const url = new URL(window.location.href);
    const tag = this.getTag(url);
    if (!url.searchParams.has('tag') && tag) {
      window.history.replaceState(window.history.state, '', this.buildFilterUrl(tag, url, false));
    }
    this.updateLinks();
    if (url.searchParams.has('tag') && tag !== (this.dataset.renderedTag || '')) {
      this.renderCategory(url, { historyMode: 'none' });
    } else {
      this.syncCategories(url);
      this.observeLoadMore();
    }
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    this.lifecycle = null;
    this.cancelRequest();
    this.observer?.disconnect();
    this.observer = null;
  }

  handleClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('[data-ag-blog-load-more], [data-ag-blog-category]');
    if (!link || !this.contains(link) || link.target === '_blank') return;
    event.preventDefault();
    if (link.matches('[data-ag-blog-load-more]')) this.loadMore(link);
    else this.renderCategory(link.href);
  }

  getTag(destination) {
    const url = new URL(destination, window.location.origin);
    if (url.searchParams.has('tag')) return url.searchParams.get('tag') || '';
    const prefix = `${new URL(this.dataset.blogUrl, window.location.origin).pathname}/tagged/`;
    return url.pathname.startsWith(prefix) ? decodeURIComponent(url.pathname.slice(prefix.length)) : '';
  }

  buildFilterUrl(tag, destination = window.location.href, resetPage = true) {
    const url = new URL(destination, window.location.origin);
    url.pathname = new URL(this.dataset.blogUrl, window.location.origin).pathname;
    if (tag) url.searchParams.set('tag', tag);
    else url.searchParams.delete('tag');
    url.searchParams.delete('section_id');
    if (resetPage) url.searchParams.delete('page');
    return url;
  }

  updateLinks() {
    this.querySelectorAll('[data-ag-blog-category]').forEach((link) => {
      link.href = this.buildFilterUrl(link.dataset.agBlogTag || '').href;
    });
    const more = this.querySelector('[data-ag-blog-load-more]');
    if (more) {
      const url = this.buildFilterUrl(this.getTag(more.href));
      const page = new URL(more.href).searchParams.get('page');
      if (page) url.searchParams.set('page', page);
      more.href = url.href;
    }
  }

  async fetchSection(destination, request) {
    const tag = this.getTag(destination);
    const url = this.buildFilterUrl(tag, destination, false);
    if (tag) url.pathname += `/tagged/${tag.split('+').map(encodeURIComponent).join('+')}`;
    url.searchParams.delete('tag');
    url.searchParams.set('section_id', this.dataset.sectionId);
    const response = await fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: request.signal,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const root = [...parsed.querySelectorAll('ag-main-blog')].find((element) => element.dataset.sectionId === this.dataset.sectionId);
    if (!root) throw new Error('Blog section was not found in the response.');
    return root;
  }

  async renderCategory(destination, { historyMode = 'push' } = {}) {
    const url = this.buildFilterUrl(this.getTag(destination), destination, false);
    this.cancelRequest();
    this.observer?.disconnect();
    const request = new AbortController();
    this.request = request;
    this.setBusy(true);
    this.setStatus(this.dataset.loadingText);
    try {
      const root = await this.fetchSection(url, request);
      if (this.request !== request || !this.isConnected) return;
      const results = this.querySelector('[data-ag-blog-results]');
      const nextResults = root.querySelector('[data-ag-blog-results]');
      if (!results || !nextResults) throw new Error('Blog results were not found.');
      results.replaceWith(nextResults);
      this.dataset.renderedTag = this.getTag(url);
      this.syncCategories(url);
      if (root.dataset.pageTitle) document.title = root.dataset.pageTitle;
      if (historyMode === 'push' && url.href !== window.location.href) {
        window.history.pushState(window.history.state, '', url);
      }
      this.updateLinks();
      this.setStatus(this.dataset.loadedText);
    } catch (error) {
      if (error.name !== 'AbortError' && this.request === request) this.setStatus(this.dataset.errorText, true);
    } finally {
      if (this.request === request) {
        this.request = null;
        this.setBusy(false);
        this.observeLoadMore();
      }
    }
  }

  syncCategories(destination) {
    const tag = this.getTag(destination);
    this.querySelectorAll('.ag-main-blog__category').forEach((link) => {
      const linkTag = link.dataset.agBlogTag || '';
      const active = linkTag ? tag.split('+').includes(linkTag) : !tag;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }

  async loadMore(link = this.querySelector('[data-ag-blog-load-more]')) {
    if (!link || this.request) return;
    this.observer?.disconnect();
    const request = new AbortController();
    this.request = request;
    this.setBusy(true);
    this.setStatus(this.dataset.loadingText);
    this.setLinkLoading(link, true);
    let observeNext = false;
    try {
      const root = await this.fetchSection(link.href, request);
      if (this.request !== request || !this.isConnected) return;
      const grid = this.querySelector('[data-ag-blog-grid]');
      const nextGrid = root.querySelector('[data-ag-blog-grid]');
      if (!grid || !nextGrid || !nextGrid.children.length) throw new Error('The next article grid was not found.');
      const existingIds = new Set([...grid.children].map((card) => card.dataset.articleId));
      const cards = [...nextGrid.children].filter((card) => !existingIds.has(card.dataset.articleId));
      if (!cards.length) throw new Error('The next batch contains no new articles.');
      const hadFocus = link === document.activeElement;
      grid.append(...cards);
      const wrap = this.querySelector('[data-ag-blog-load-more-wrap]');
      const nextWrap = root.querySelector('[data-ag-blog-load-more-wrap]');
      if (nextWrap && wrap) wrap.replaceWith(nextWrap);
      else wrap?.remove();
      this.updateLinks();
      if (hadFocus) cards[0].focus({ preventScroll: true });
      this.setStatus(this.dataset.loadedText);
      observeNext = true;
    } catch (error) {
      if (error.name !== 'AbortError' && this.request === request) this.setStatus(this.dataset.errorText, true);
    } finally {
      if (this.request === request) {
        this.request = null;
        this.setLinkLoading(link, false);
        this.setBusy(false);
        if (observeNext) this.observeLoadMore();
      }
    }
  }

  observeLoadMore() {
    this.observer?.disconnect();
    this.observer = null;
    const wrap = this.querySelector('[data-ag-blog-load-more-wrap]');
    if (!wrap || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
    }, { rootMargin: '300px 0px', threshold: 0 });
    this.observer.observe(wrap);
  }

  cancelRequest() {
    this.request?.abort();
    this.request = null;
    this.setLinkLoading(this.querySelector('[data-ag-blog-load-more]'), false);
    this.setBusy(false);
  }

  setLinkLoading(link, loading) {
    if (!link) return;
    if (loading) link.setAttribute('aria-disabled', 'true');
    else link.removeAttribute('aria-disabled');
    link.querySelector('[data-ag-blog-spinner]')?.classList.toggle('hidden', !loading);
  }

  setBusy(busy) {
    this.setAttribute('aria-busy', String(busy));
    this.querySelector('[data-ag-blog-results]')?.setAttribute('aria-busy', String(busy));
  }

  setStatus(message, isError = false) {
    const status = this.querySelector('[data-ag-blog-status]');
    if (!status) return;
    status.textContent = message || '';
    status.classList.toggle('is-error', isError);
  }
}

if (!customElements.get('ag-main-blog')) {
  customElements.define('ag-main-blog', AgMainBlog);
}
